import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, ExternalLink, Megaphone, Eye, MousePointerClick, Calendar } from "lucide-react";

const SWATCHES = [
  { bg: "#0f172a", accent: "#fbbf24", label: "Night + Amber" },
  { bg: "#065f46", accent: "#a7f3d0", label: "Forest + Mint" },
  { bg: "#7c2d12", accent: "#fed7aa", label: "Rust + Cream" },
  { bg: "#1e3a8a", accent: "#bfdbfe", label: "Deep Blue" },
  { bg: "#581c87", accent: "#e9d5ff", label: "Plum" },
  { bg: "#991b1b", accent: "#fecaca", label: "Brick" },
];

const BLANK = { business: "", tagline: "", cta_url: "", bg: SWATCHES[0].bg, accent: SWATCHES[0].accent, category: "any", start_at: "", end_at: "" };

export default function AdminAds() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...ad} = edit
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/is-admin")
      .then(({ data }) => {
        setIsAdmin(data.is_admin);
        if (!data.is_admin) { toast.error("Admin access required"); navigate("/feed"); return; }
        return Promise.all([api.get("/ads/admin"), api.get("/categories")]);
      })
      .then((results) => {
        if (!results) return;
        setAds(results[0].data);
        setCategories(results[1].data);
      })
      .catch(() => { toast.error("Admin check failed"); navigate("/feed"); });
  }, [navigate]);

  const openCreate = () => { setForm(BLANK); setEditing({}); };
  const openEdit = (ad) => {
    setForm({
      ...ad,
      category: ad.category || "any",
      start_at: ad.start_at ? toLocalInput(ad.start_at) : "",
      end_at: ad.end_at ? toLocalInput(ad.end_at) : "",
    });
    setEditing(ad);
  };
  const close = () => { setEditing(null); setForm(BLANK); };

  function toLocalInput(iso) {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  }
  function toIso(local) {
    if (!local) return null;
    const d = new Date(local);
    return isNaN(d) ? null : d.toISOString();
  }
  function windowLabel(ad) {
    const s = ad.start_at ? new Date(ad.start_at) : null;
    const e = ad.end_at ? new Date(ad.end_at) : null;
    if (!s && !e) return "Always on";
    const fmt = (d) => d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "–";
    return `${fmt(s)} → ${fmt(e)}`;
  }
  function isLiveNow(ad) {
    if (!ad.active) return false;
    const now = new Date();
    if (ad.start_at && new Date(ad.start_at) > now) return false;
    if (ad.end_at && new Date(ad.end_at) < now) return false;
    return true;
  }

  const save = async () => {
    if (!form.business || !form.tagline || !form.cta_url) return toast.error("Business, tagline and link are required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        category: form.category === "any" ? null : form.category,
        start_at: toIso(form.start_at),
        end_at: toIso(form.end_at),
      };
      if (editing && editing.ad_id) {
        const { data } = await api.patch(`/ads/${editing.ad_id}`, payload);
        setAds((prev) => prev.map((a) => (a.ad_id === data.ad_id ? data : a)));
        toast.success("Ad updated");
      } else {
        const { data } = await api.post("/ads", payload);
        setAds((prev) => [data, ...prev]);
        toast.success("Ad created");
      }
      close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (ad) => {
    try {
      const { data } = await api.patch(`/ads/${ad.ad_id}`, { active: !ad.active });
      setAds((prev) => prev.map((a) => (a.ad_id === data.ad_id ? data : a)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete the "${ad.business}" ad?`)) return;
    try {
      await api.delete(`/ads/${ad.ad_id}`);
      setAds((prev) => prev.filter((a) => a.ad_id !== ad.ad_id));
      toast.success("Ad deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isAdmin === null) return <div className="text-slate-500">Loading…</div>;
  if (!isAdmin) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <Megaphone size={28} className="text-[#3E2A4E]" /> Ads Console
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Add, edit and rotate local business sponsors shown in the feed.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-ad-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full px-5 btn-primary-glow">
          <Plus size={16} className="mr-1" /> New ad
        </Button>
      </div>

      {ads.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-2xl border border-slate-200/80">
          No ads yet — hit "New ad" to add your first sponsor.
        </div>
      ) : (
        <div className="grid gap-4">
          {ads.map((ad) => (
            <div key={ad.ad_id} data-testid={`admin-ad-${ad.ad_id}`} className="bg-white border border-slate-200/80 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4">
              <div className="rounded-xl p-4 flex-1" style={{ background: ad.bg }}>
                <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: ad.accent }}>Sponsored · Local business</div>
                <div className="font-heading text-base sm:text-lg font-extrabold text-white mt-1 break-words">{ad.business}</div>
                <div className="text-sm text-white/80 mt-0.5 break-words">{ad.tagline}</div>
                <a href={ad.cta_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: ad.accent, color: ad.bg }}>
                  Visit <ExternalLink size={11} />
                </a>
              </div>
              <div className="flex flex-row sm:flex-col justify-between gap-3 sm:min-w-[200px]">
                <div>
                  <div className="text-xs text-slate-500">Category</div>
                  <div className="text-sm font-semibold capitalize">{ad.category || "any"}</div>
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Calendar size={11} /> {windowLabel(ad)}</div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1" title="Impressions"><Eye size={12} /> {ad.impressions || 0}</span>
                    <span className="inline-flex items-center gap-1" title="Clicks"><MousePointerClick size={12} /> {ad.clicks || 0}</span>
                    {(ad.impressions || 0) > 0 && (
                      <span className="font-mono-money text-[10px] text-slate-500">CTR {((ad.clicks || 0) / ad.impressions * 100).toFixed(1)}%</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Switch checked={ad.active} onCheckedChange={() => toggleActive(ad)} data-testid={`toggle-active-${ad.ad_id}`} />
                    <span className={`text-xs font-semibold ${isLiveNow(ad) ? "text-emerald-700" : ad.active ? "text-amber-700" : "text-slate-400"}`}>
                      {isLiveNow(ad) ? "Live now" : ad.active ? "Scheduled" : "Paused"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(ad)} data-testid={`edit-ad-${ad.ad_id}`} className="rounded-full">
                    <Pencil size={13} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(ad)} data-testid={`delete-ad-${ad.ad_id}`} className="rounded-full text-red-600 hover:text-red-700">
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="ad-editor-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.ad_id ? "Edit ad" : "New ad"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Business name</Label>
              <Input value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="mt-1 h-11" data-testid="ad-business-input" />
            </div>
            <div>
              <Label>Tagline</Label>
              <Textarea value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} rows={2} className="mt-1" data-testid="ad-tagline-input" />
            </div>
            <div>
              <Label>Link (CTA URL)</Label>
              <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="https://…" className="mt-1 h-11" data-testid="ad-url-input" />
            </div>
            <div>
              <Label>Shown for category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="ad-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any category</SelectItem>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start_at">Start (optional)</Label>
                <Input id="start_at" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="mt-1 h-11" data-testid="ad-start-input" />
              </div>
              <div>
                <Label htmlFor="end_at">End (optional)</Label>
                <Input id="end_at" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="mt-1 h-11" data-testid="ad-end-input" />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2">Leave both blank for always-on. Set a window for Fri–Sun placements — the ad hides itself outside it.</p>
            <div>
              <Label>Color scheme</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {SWATCHES.map((s) => (
                  <button
                    key={s.bg}
                    type="button"
                    onClick={() => setForm({ ...form, bg: s.bg, accent: s.accent })}
                    data-testid={`swatch-${s.bg.replace("#","")}`}
                    className={`rounded-xl p-3 text-left transition-all border-2 ${form.bg === s.bg ? "border-slate-900 scale-[1.02]" : "border-transparent"}`}
                    style={{ background: s.bg }}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: s.accent }}>Preview</div>
                    <div className="text-xs font-bold text-white mt-0.5 truncate">{s.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-ad-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold">
              {saving ? "Saving…" : editing?.ad_id ? "Save changes" : "Create ad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
